import React from 'react';
import { useTranslation } from 'react-i18next';
import Calendar from '../../Calendar';
import EventsList from '../../EventsList';
import { User } from '../../types';

interface CalendarSectionProps {
  user: User;
  events: any[];
  calendarViewMode: 'calendar' | 'list';
  setCalendarViewMode: (mode: 'calendar' | 'list') => void;
  dateRangeFilter: { from: string; to: string };
  setDateRangeFilter: (filter: { from: string; to: string }) => void;
  eventSearchTerm: string;
  setEventSearchTerm: (term: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  eventsPerPage: number;
  currentDate: Date;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  setShowCreateEventModal: (show: boolean) => void;
  getEventsForDate: (date: Date) => any[];
  getCalendarDays: () => Date[];
  navigateMonth: (direction: 'prev' | 'next') => void;
  switchToListView: (date: Date) => void;
  openEventDetailsModal: (event: any) => void;
  handleEventStatusChange: (eventId: string, status: 'active' | 'completed' | 'cancelled') => void;
  handleSyncSingleEvent: (event: any) => void;
  handleDeleteEvent: (eventId: string) => void;
  getFilteredEvents: () => any[];
  getPaginatedEvents: () => any[];
}

export const CalendarSection: React.FC<CalendarSectionProps> = ({
  user,
  events,
  calendarViewMode,
  setCalendarViewMode,
  dateRangeFilter,
  setDateRangeFilter,
  eventSearchTerm,
  setEventSearchTerm,
  currentPage,
  setCurrentPage,
  eventsPerPage,
  currentDate,
  selectedDate,
  setSelectedDate,
  setShowCreateEventModal,
  getEventsForDate,
  getCalendarDays,
  navigateMonth,
  switchToListView,
  openEventDetailsModal,
  handleEventStatusChange,
  handleSyncSingleEvent,
  handleDeleteEvent,
  getFilteredEvents,
  getPaginatedEvents
}) => {
  const { t } = useTranslation();

  return (
    <div className="calendar-section">
      <div className="calendar-header">
        <div className="calendar-view-toggle">
          <button 
            onClick={() => setCalendarViewMode('calendar')}
            className={`view-toggle-btn ${calendarViewMode === 'calendar' ? 'active' : ''}`}
          >
            📅 {t('calendar.calendarView')}
          </button>
          <button 
            onClick={() => setCalendarViewMode('list')}
            className={`view-toggle-btn ${calendarViewMode === 'list' ? 'active' : ''}`}
          >
            📋 {t('calendar.listView')}
          </button>
        </div>
        
        <div className="calendar-actions">
          <button 
            onClick={() => setShowCreateEventModal(true)}
            className="create-event-btn"
            style={{
              backgroundColor: '#007acc',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            + {t('calendar.createEvent')}
          </button>
        </div>
      </div>
      
      {calendarViewMode === 'calendar' ? (
        <Calendar
          user={user}
          events={events}
          getEventsForDate={getEventsForDate}
          getCalendarDays={getCalendarDays}
          navigateMonth={navigateMonth}
          currentDate={currentDate}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          switchToListView={switchToListView}
          openEventDetailsModal={openEventDetailsModal}
          handleEventStatusChange={handleEventStatusChange}
          handleSyncSingleEvent={handleSyncSingleEvent}
          handleDeleteEvent={handleDeleteEvent}
          setShowCreateEventModal={setShowCreateEventModal}
        />
      ) : (
        <EventsList
          user={user}
          events={events}
          dateRangeFilter={dateRangeFilter}
          setDateRangeFilter={setDateRangeFilter}
          eventSearchTerm={eventSearchTerm}
          setEventSearchTerm={setEventSearchTerm}
          setShowCreateEventModal={setShowCreateEventModal}
          getFilteredEvents={getFilteredEvents}
          getPaginatedEvents={getPaginatedEvents}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil(getFilteredEvents().length / eventsPerPage)}
          openEventDetailsModal={openEventDetailsModal}
          handleEventStatusChange={handleEventStatusChange}
          handleDeleteEvent={handleDeleteEvent}
        />
      )}
    </div>
  );
};


